import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { BASE } from "../lib/api";
import { AuthShell, Icon } from "../components/AuthShell";

const GENERIC_MESSAGE = "If that account exists, a reset link has been sent.";

/**
 * The public "forgot password?" entry point. Deliberately thin: it only ever
 * shows one of two things — the form, or the fixed generic confirmation —
 * regardless of what the server actually did. The API already guarantees an
 * identical response whether the address belongs to an account or not; this
 * page just has to not undo that by branching on anything itself.
 *
 * Uses a raw fetch rather than the shared `api` client, same reasoning as
 * InviteAccept: there is no session yet, and the client's 401-handling side
 * effects don't apply to a pre-auth action.
 */
export const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [networkError, setNetworkError] = useState<string | null>(null);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setNetworkError(null);
        try {
            await fetch(`${BASE}/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            // The response body is always the same generic message on any
            // status the endpoint can return — there is nothing in it worth
            // reading, so submission itself is the only signal this page acts on.
            setSubmitted(true);
        } catch {
            // A genuine failure to reach the server at all — the one case
            // worth a distinct message, since it says nothing about whether
            // an account exists.
            setNetworkError("Could not reach the server. Check your connection and try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell eyebrow="Account recovery" title="Reset password">
            {submitted ? (
                <div className="tcl-form">
                    <p className="tcl-lead-text">{GENERIC_MESSAGE}</p>
                    <p className="tcl-hint">Check your inbox — the link works once and expires in 60 minutes.</p>
                    <Link to="/login" className="tcl-btn tcl-btn--ghost">
                        <Icon><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Icon>
                        Back to sign in
                    </Link>
                </div>
            ) : (
                <form className="tcl-form" onSubmit={(e) => void submit(e)} noValidate>
                    <p className="tcl-lead-text">Enter your account's email and we'll send a link to reset your password.</p>

                    {networkError && <div className="tcl-error">{networkError}</div>}

                    <div className="tcl-field">
                        <label htmlFor="fp-email">Email address</label>
                        <div className="tcl-control">
                            <span className="tcl-lead">
                                <Icon><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></Icon>
                            </span>
                            <input id="fp-email" type="email" value={email}
                                   onChange={(e) => setEmail(e.target.value)}
                                   placeholder="you@trustcare.com.au" autoComplete="username" required />
                        </div>
                    </div>

                    <button className="tcl-btn" type="submit" disabled={busy || email.length === 0}>
                        <Icon><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></Icon>
                        {busy ? "Sending…" : "Send reset link"}
                    </button>

                    <p className="tcl-backlink"><Link to="/login">Back to sign in</Link></p>
                </form>
            )}
        </AuthShell>
    );
};
